import { createContext, useContext, useEffect, useState, startTransition, type ReactNode } from 'react';
import { loadHelpHtml } from './helpContent';

interface HelpContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  currentRoute: string;
  setCurrentRoute: (route: string) => void;
  helpContent: string;
  isLoading: boolean;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function useHelp() {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within HelpProvider');
  return ctx;
}

export function HelpProvider({ children, initialRoute = '' }: { children: ReactNode; initialRoute?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(initialRoute || window.location.hash || '#/sales/invoices');
  const [helpContent, setHelpContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function loadHelpContent(route: string, signal: AbortSignal) {
    startTransition(() => setIsLoading(true));
    try {
      const html = loadHelpHtml(route);
      if (signal.aborted) return;
      startTransition(() => setHelpContent(html));
    } catch {
      if (signal.aborted) return;
      startTransition(() => setHelpContent('<p class="text-slate-400">Help content unavailable.</p>'));
    } finally {
      startTransition(() => setIsLoading(false));
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    void loadHelpContent(currentRoute, controller.signal);
    return () => controller.abort();
  }, [currentRoute]);

  useEffect(() => {
    const onHashChange = () => {
      const newRoute = window.location.hash || '#/sales/invoices';
      setCurrentRoute(newRoute);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <HelpContext.Provider value={{ isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false), toggle: () => setIsOpen(o => !o), currentRoute, setCurrentRoute, helpContent, isLoading }}>
      {children}
    </HelpContext.Provider>
  );
}
